import { useEffect, useRef } from "react";
import { NavLink, Outlet, useLocation, useMatches, useNavigation } from "react-router";

import estilos from "./Layout.module.css";
import { BotaoTema } from "./BotaoTema";
import { Marca } from "./Marca";

/** Rotas que usam a área de trabalho em três painéis marcam `handle: { largura: "ampla" }`. */
function useLarguraAmpla(): boolean {
  return useMatches().some(
    (m) => (m.handle as { largura?: string } | undefined)?.largura === "ampla",
  );
}

/**
 * Frame of every page. On each route change, focus moves to the target the page marks with
 * `data-initial-focus` (the new-analysis box) or to the `<h1>`, so screen reader and keyboard
 * users are not left on the link they clicked. On first load only a marked target gets focus.
 * The page may arrive lazily, so focus waits for it.
 */
export function Layout() {
  const { pathname } = useLocation();
  const navegacao = useNavigation();
  const principal = useRef<HTMLElement>(null);
  const primeira = useRef(true);
  const ampla = useLarguraAmpla();

  useEffect(() => {
    const firstLoad = primeira.current;
    primeira.current = false;
    // On touch screens a marked target is not focused by itself: it would open the keyboard.
    const touch = matchMedia("(pointer: coarse)").matches;
    // The page may arrive lazily after the route change: keep trying for up to 2 s.
    let attempts = 40;
    let timer = 0;
    const attempt = () => {
      const root = principal.current;
      const target =
        (touch ? null : root?.querySelector<HTMLElement>("[data-initial-focus]")) ??
        (firstLoad ? null : root?.querySelector("h1"));
      if (target) target.focus();
      else if (--attempts) timer = window.setTimeout(attempt, 50);
    };
    timer = window.setTimeout(attempt, 0);
    return () => {
      clearTimeout(timer);
    };
  }, [pathname]);

  return (
    <>
      <a className={estilos.pular} href="#conteudo">
        Pular para o conteúdo
      </a>
      {/* Na mesa (rotas amplas), marca e tema vão para o trilho, como no ChatGPT. */}
      {!ampla && (
        <header className={estilos.topo}>
          <Marca />
          <nav aria-label="Principal" className={estilos.nav}>
            <NavLink to="/metodo">Como funciona</NavLink>
            <NavLink to="/selecao">Registro</NavLink>
            <BotaoTema />
          </nav>
        </header>
      )}
      <main
        id="conteudo"
        ref={principal}
        className={ampla ? estilos.ampla : estilos.estreita}
        aria-busy={navegacao.state === "loading"}
      >
        <Outlet />
      </main>
    </>
  );
}
